import { InvestmentDetailsInterface } from "../../../models";
import { CreateDealRequest } from "../../../service/fund/Types";
import { convertDateToStringYYYYMMDD, INVALID_DATE_OBJECT_STRING_SUFFIX } from "../../../utils/date";

export type DealSpecificData = CreateDealRequest["dealSpecificData"];

type MapInvestmentsToDealSpecificDataOptions = {
  transformAssociations?: (associations: string[], investment: InvestmentDetailsInterface) => string[];
};

export const getEmptyDealSpecificData = (): DealSpecificData => ({
  equity: [],
  landAcquisition: [],
  loan: [],
  revenueShare: [],
  unitPurchase: [],
});

const transformDatesToDateOnlyStrings = (value: unknown): unknown => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? `${value.toString()}${INVALID_DATE_OBJECT_STRING_SUFFIX}`
      : convertDateToStringYYYYMMDD(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => transformDatesToDateOnlyStrings(item));
  }

  if (value !== null && typeof value === "object") {
    return Object.entries(value).reduce<Record<string, unknown>>((acc, [key, nestedValue]) => {
      acc[key] = transformDatesToDateOnlyStrings(nestedValue);
      return acc;
    }, {});
  }

  return value;
};

const buildEntryFromInvestment = (investment: InvestmentDetailsInterface, associationUuids: string[]): string => {
  const entry: Record<string, unknown> = {
    relatedObjects: associationUuids.map((uuid) => ({ uuid })),
  };

  if (investment.description !== undefined) {
    entry.description = investment.description ?? null;
  }

  if (investment.fields) {
    Object.entries(investment.fields).forEach(([key, value]) => {
      entry[key] = value;
    });
  }

  return JSON.stringify(transformDatesToDateOnlyStrings(entry));
};

export const mapInvestmentsToDealSpecificData = (
  investments: InvestmentDetailsInterface[],
  options?: MapInvestmentsToDealSpecificDataOptions
): DealSpecificData => {
  const data = getEmptyDealSpecificData();

  investments.forEach((investment) => {
    const dealTypeKey = investment.instrument as keyof DealSpecificData | undefined;
    if (!dealTypeKey || !(dealTypeKey in data)) return;

    const associations = options?.transformAssociations
      ? options.transformAssociations([...(investment.associations ?? [])], investment)
      : [...(investment.associations ?? [])];

    (data[dealTypeKey] as string[]).push(buildEntryFromInvestment(investment, associations));
  });

  return data;
};
